import { Document, FieldDataType, SchemaField, SystemFieldNames } from '../../../core/store/models/document.model';
import { Layer, LayerTypes, QuickFilterSelection, RenderLensMode } from '../../store/cop/models';
import { Dataset, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { DocumentQueryParams } from 'src/app/modules/core/store/models/query.model';
import { Filter, FilterGroup, FilterGroupType, RuleOperator } from 'src/app/modules/core/store/models/filters.model';
import { MapPosition } from '../../drawing/models/map-position.model';
import { QuickFilterHelperService } from '../quick-filter-helper.service';

export const DEFAULT_MAX_DOCUMENT_COUNT = 1000;
export const AUTO_CLUSTER_NUMBER = 10000;
export const MAX_DOCUMENT_COUNT = 100000;
export const MAX_FLIGHT_ASSET_DOCUMENT_COUNT = 2000;

const SIGNALR_DATASET_TYPES = [DatasetType.Alerts, DatasetType.Alarm, DatasetType.Incident];

const POLLING_EXCLUDE_DATASET_TYPES = [DatasetType.Metrics, DatasetType.ActivityLog, DatasetType.Picklist];

export class MapLayerQueryBuildService {
  constructor() {}

  public static get autoClusterNumber(): number {
    return AUTO_CLUSTER_NUMBER;
  }

  public static get maxDocumentCount(): number {
    return MAX_DOCUMENT_COUNT > 0 ? MAX_DOCUMENT_COUNT : null;
  }

  public static get autoClusterDocuments(): boolean {
    return MapLayerQueryBuildService.autoClusterNumber > 0;
  }

  public static buildLayerQueryParams(
    layer: Layer,
    dataset: Dataset,
    layerQuickFilter: QuickFilterSelection,
    floorFilter: Filter,
    mapPosition: MapPosition
  ): DocumentQueryParams {
    const maxCount =
      dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Flight
        ? MAX_FLIGHT_ASSET_DOCUMENT_COUNT
        : MAX_DOCUMENT_COUNT;
    const pagination = {
      skip: 0,
      take: !MapLayerQueryBuildService.autoClusterDocuments
        ? (layer.displayOptions?.maxItemsToRender ?? DEFAULT_MAX_DOCUMENT_COUNT)
        : maxCount
    };

    let filterGroup = layer.rule?.root;
    if (filterGroup && filterGroup.filterGroups.length === 0 && filterGroup.filters.length === 0) {
      filterGroup = null;
    }

    if (!!floorFilter) {
      // make sure that a filter group exists if a floor is present
      if (!filterGroup) {
        filterGroup = {
          filters: [floorFilter],
          filterGroups: [],
          groupType: FilterGroupType.And
        };
      } else {
        // Encapsulate filter group so that floor filter is not ignored when the layer filter group is of type OR
        let newFilter = {
          filters: [floorFilter],
          filterGroups: [filterGroup],
          groupType: FilterGroupType.And
        };

        filterGroup = newFilter;
      }
    }

    // handle quick filters if they are active
    filterGroup = QuickFilterHelperService.addQuickFiltersIfActive(filterGroup, layer, dataset, layerQuickFilter);

    // if this is a flight layer then we should include the map position
    // in the filter criteria
    if (!!mapPosition && this.isUsingBoundingBoxUpdateQuery(layer, dataset)) {
      let geoFields = layer?.displayOptions?.geoFieldsToDisplay;

      if (!geoFields) {
        let geometryFields = dataset?.schema?.fields?.filter(f => f.dataType === FieldDataType.Geometry);

        let fieldName = 'geometry';

        if (geometryFields?.length > 0 && !!geometryFields[0].systemName) {
          fieldName = geometryFields[0].systemName;
        }

        filterGroup = {
          filterGroups: filterGroup ? [filterGroup] : [],
          filters: [
            {
              field: fieldName,
              operator: RuleOperator.InBoundingBox,
              value: `${mapPosition.southWest.lng},${mapPosition.northEast.lat},${mapPosition.northEast.lng},${mapPosition.southWest.lat}`
            }
          ],
          groupType: FilterGroupType.And
        };
      } else {
        let geoFilters: Filter[] = [];
        geoFields.forEach(element => {
          let filter: Filter = {
            field: element,
            operator: RuleOperator.InBoundingBox,
            value: `${mapPosition.southWest.lng},${mapPosition.northEast.lat},${mapPosition.northEast.lng},${mapPosition.southWest.lat}`
          };

          geoFilters.push(filter);
        });

        let geoFilterGroup: FilterGroup = {
          filterGroups: [],
          filters: geoFilters,
          groupType: FilterGroupType.Or
        };

        let encapsulatedFilterGroup = {
          groupType: FilterGroupType.And,
          filters: [],
          filterGroups: []
        };

        if (!!filterGroup) {
          encapsulatedFilterGroup.filterGroups = [filterGroup];
        }

        encapsulatedFilterGroup.filterGroups.push(geoFilterGroup);

        filterGroup = encapsulatedFilterGroup;
      }
    }

    const calcFields = [];

    const fields = MapLayerQueryBuildService.buildFieldsForQuery(layer, dataset, filterGroup);

    const queryParams = {
      filterGroup,
      calcFields,
      fields: fields.join(','),
      pagination
    };

    return queryParams;
  }

  public static isUsingBoundingBoxUpdateQuery(layer: Layer, dataset: Dataset): boolean {
    return (
      (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Flight) ||
      layer?.displayOptions?.renderLensMode === RenderLensMode.viewport
    );
  }

  public static isUsingSignalRDocumentUpdates(dataset: Dataset): boolean {
    return (
      SIGNALR_DATASET_TYPES.includes(dataset?.type) ||
      (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Person) ||
      (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.HorizonPerson)
    );
  }

  public static isUsingPollingQuery(layer: Layer, dataset: Dataset): boolean {
    if (SIGNALR_DATASET_TYPES.includes(dataset?.type)) {
      return false;
    }

    if (POLLING_EXCLUDE_DATASET_TYPES.includes(dataset?.type)) {
      return false;
    }

    if (
      dataset?.useTileServer ||
      (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Flight) ||
      (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Person) ||
      (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.HorizonPerson)
    ) {
      return false;
    }

    // if the layer is slaved to the viewport then data will be updated when the
    // viewport changes
    if (layer?.displayOptions?.renderLensMode === RenderLensMode.viewport) {
      return false;
    }

    return true;
  }

  public static buildFieldsForQuery(layer: Layer, dataset: Dataset, filterGroup: FilterGroup): string[] {
    const fields = [
      SystemFieldNames.uId,
      SystemFieldNames.workflowUId,
      SystemFieldNames.workflowStepUId,
      SystemFieldNames.borderColor,
      SystemFieldNames.fillColor,
      SystemFieldNames.dateCreated,
      SystemFieldNames.dateUpdated
    ];

    // check to see if geo simplification is in use
    const geoSimplificationLevel = this.getLayerGeoSimplicationLevel(layer, dataset);

    // add the geometry fields to display
    const geoFieldsToAdd = this.getDatasetGeoFieldNames(layer, dataset);

    for (let field of geoFieldsToAdd) {
      fields.push(!!geoSimplificationLevel ? `${field}.${geoSimplificationLevel}_${field}` : field);
    }

    const labelFields = this.getDatasetLabelFields(dataset);
    if (labelFields) {
      labelFields.map(f => fields.push(f));
    }

    dataset?.schema?.fields.filter(f => f.dataType === FieldDataType.RelatedParent).map(f => fields.push(f.systemName));

    var specificFields = this.getDatasetSpecificFields(dataset);
    if (specificFields) {
      fields.push(...specificFields);
    }

    // include the fields that are used in filters in the fields that are
    // returned. This is needed for the browser filter runtime code to work
    const fieldsUsedInFilters = this.getFieldsUsedInFilters(filterGroup, dataset);
    fieldsUsedInFilters.forEach(ff => {
      if (fields.findIndex(fsn => fsn === ff)) {
        fields.push(ff);
      }
    });

    // if the layer is setup for timeline support, ensure the from/to date fields
    // are included
    if (layer.displayOptions?.showInTimeline) {
      if (layer.displayOptions?.timelineFromField) {
        fields.push(layer.displayOptions?.timelineFromField);
      }

      if (layer.displayOptions?.timelineToField) {
        fields.push(layer.displayOptions?.timelineToField);
      }
    }

    return fields;
  }

  public static generateFloorFilter(layer: Layer, dataset: Dataset, floor?: string): Filter {
    let floorFilter: Filter = null;
    if (!!layer.displayOptions?.floorFilterSourceField && !!floor) {
      const floorFieldName = layer?.displayOptions?.floorFilterSourceField;
      const floorField = dataset?.schema.fields.find(f => f.systemName === floorFieldName);
      const floorFieldValue = floorField?.picklistValues?.find(p => p.value === floor);
      if (!!floorFieldValue) {
        floorFilter = {
          uId: null,
          field: floorFieldName,
          operator: RuleOperator.Equal,
          value: floorFieldValue.uId
        };
      }
    }
    return floorFilter;
  }

  public static getFieldsUsedInFilters(filterGroup: FilterGroup, dataset: Dataset): string[] {
    if (!filterGroup || !!!dataset) {
      return [];
    }

    const fieldSystemNames = (filterGroup.filters ?? [])
      .filter(f => dataset.schema.fields.findIndex(sf => sf.systemName === f.field) >= 0)
      .map(f => f.field);

    for (let childGroup of filterGroup.filterGroups ?? []) {
      const childFilterFields = this.getFieldsUsedInFilters(childGroup, dataset);
      childFilterFields.forEach(cf => {
        if (fieldSystemNames.findIndex(fsn => fsn === cf)) {
          fieldSystemNames.push(cf);
        }
      });
    }

    return fieldSystemNames;
  }

  public static findFilterByFieldName(filterGroup: FilterGroup, fieldName: string): Filter {
    if (!filterGroup) {
      return null;
    }

    const filter = filterGroup.filters?.find(f => f.field === fieldName);
    if (!!filter) {
      return filter;
    }

    for (let childGroup of filterGroup.filterGroups ?? []) {
      const groupFilter = this.findFilterByFieldName(childGroup, fieldName);
      if (!!groupFilter) {
        return groupFilter;
      }
    }

    return null;
  }

  public static getDatasetGeoFieldNames(layer: Layer, dataset: Dataset): string[] {
    const geoFieldNamesToAdd = [];

    if (!!layer.displayOptions?.geoFieldsToDisplay && layer.displayOptions?.geoFieldsToDisplay.length > 0) {
      // use the fields from the settings
      geoFieldNamesToAdd.push(...layer.displayOptions.geoFieldsToDisplay);
    } else {
      // find the first geo field in the schema
      var geoField = dataset?.schema?.fields.find(f => f.dataType === FieldDataType.Geometry);
      if (!!geoField) {
        geoFieldNamesToAdd.push(geoField.systemName);
      }
    }

    if (!!layer.displayOptions?.optionalGeoFieldsToDisplay && layer.displayOptions?.optionalGeoFieldsToDisplay.length > 0) {
      geoFieldNamesToAdd.push(layer.displayOptions?.optionalGeoFieldsToDisplay[0]);
    }

    return geoFieldNamesToAdd;
  }

  public static getDatasetLabelFields(dataset: Dataset): string[] {
    const labels = [
      'name',
      'title',
      'summary',
      'display_name',
      'first_name last_name',
      'first last',
      'subject',
      'details',
      'description',
      'body'
    ];

    const isFlight = dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Flight;

    for (let l of labels) {
      const results = [];
      const parts = l.split(' ');
      for (let part of parts) {
        if (dataset?.schema?.fields?.find(f => f.systemName === part)) {
          results.push(!isFlight ? part : `${part}.raw`);
        }
      }

      if (results.length) {
        return results;
      }
    }

    return null;
  }

  public static getDatasetLabelFieldDisplay(document: Document, fieldNames: string[]): string {
    if (!!!fieldNames) {
      return null;
    }

    const values = [];

    for (let part of fieldNames) {
      if (!!document[part]) {
        values.push(document[part]);
      }
    }

    if (values.length) {
      return values.join(' ');
    } else {
      return null;
    }
  }

  public static getLayerGeoSimplicationLevel(layer: Layer, dataset: Dataset) {
    const geoSimpOptions = ['simp100', 'simp1', null];

    let geoSimp = null;

    if (dataset?.type === DatasetType.Alerts && dataset.subtype === DatasetSubtype.Weather) {
      if (layer?.displayOptions?.weatherGeometryLevel !== undefined && layer?.displayOptions?.weatherGeometryLevel > 0) {
        geoSimp = geoSimpOptions[layer?.displayOptions?.weatherGeometryLevel - 1];
      } else {
        geoSimp = 'simp100';
      }
    }

    return geoSimp;
  }

  private static getDatasetSpecificFields(dataset: Dataset): string[] {
    let fields = [];

    if (dataset?.subtype == DatasetSubtype.PowerOutage) {
      fields = ['outage_count', 'tracked_count', 'region_type', 'county_id', 'county_name'];
    } else if (dataset?.subtype === DatasetSubtype.PowerOutageV2) {
      fields = ['customers_out', 'estimated_restoration'];
    } else if (dataset?.subtype == DatasetSubtype.TropicalCyclone) {
      fields = [
        'track',
        'forecast',
        'is_active',
        'position_storm_cat',
        'position_max_wind_speed_kph',
        'position_max_gust_speed_kph',
        'position_max_wind_speed_mph',
        'position_max_gust_speed_mph',
        'position_mvmt_speed_kph',
        'position_mvmt_speed_mph',
        'position_mvmt_direction',
        'position_storm_short_name'
      ];
    } else if (dataset?.subtype == DatasetSubtype.Flight) {
      fields = ['heading_direction', 'aircraft_type', 'aircraft_type_code', 'wtc', 'on_ground', 'flight_positions'];
    } else if (dataset?.subtype === DatasetSubtype.TravelItinerary) {
      fields = ['segments'];
    } else if (dataset?.subtype === DatasetSubtype.Earthquake) {
      fields = ['magnitude', 'start_date', 'shake_map_geometry'];
    } else if (dataset?.subtype === DatasetSubtype.Weather) {
      fields = ['color'];
    } else if (dataset?.type === DatasetType.Camera && dataset?.subtype === DatasetSubtype.Traffic) {
      fields = ['camera_id', 'region_id', 'state_id', 'out_of_service', 'is_camera_cluster'];
    } else if (dataset?.type === DatasetType.Camera && dataset?.subtype === DatasetSubtype.Group) {
      fields = ['field_of_view'];
    } else if (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Vehicle) {
      fields = ['heading_degrees', 'engine_state', 'speed_mph'];
    } else if (dataset?.subtype === DatasetSubtype.HorizonLocationIntelRatings) {
      fields = ['external_id'];
    } else if (dataset?.type === DatasetType.Assets && dataset?.subtype === DatasetSubtype.Transports) {
      fields = ['position_list', 'segments', 'alarm_list'];
    }

    return fields;
  }
}
